import React from "react";
import { Col } from "react-bootstrap";
import { FormikProps } from "formik";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { VfxLib } from "../../../assets/vfx";
import Control from "../../../assets/vfx/controls/control";

import { useParams } from "react-router-dom";
import { GRID_ACTION_TYPES, sortBy } from "../../../assets/types/common";
import { Constants } from "../../../assets/utils/lib/constants";
import { ControlDatagridActionButtonProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import Form from "../../../assets/vfx/form/form";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { GridUserDto, SearchUserDto } from "../../../services/dto";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { UserService } from "./userService";
import { ViewProps } from "./props";

interface UserPageProps {
  controlStyles?: IControlStyles | undefined;
  onCustomEvent?: (
    actionType: string,
    dataId: string,
    userName?: string | undefined
  ) => void;
  lookups: ViewProps.UserLookupProps | undefined;
  isCommunityDeactive?: boolean | undefined;
}

interface UserSearchForm {
  name: string | undefined;
  communityId: string | undefined;
}

export const UserPage = (props: UserPageProps) => {
  const { communityId } = useParams();
  let searchUser: SearchUserDto = {
    sortBy: sortBy,
    pageNumber: 1,
    pageSize: 10,
  };

  if (communityId) {
    searchUser.communityId = communityId;
  }
  const [searchQuery, setSearchQuery] =
    React.useState<SearchUserDto>(searchUser);
  const userGridQuery = UserService.gridQuery(searchQuery);
  const isGridLoading = userGridQuery.isLoading || userGridQuery.isFetching;
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const getUpdatedSearchQuery = (values: UserSearchForm): SearchUserDto => {
    let selectedCommunity: string | undefined = undefined;
    if (communityId && communityId !== Constants.emptyGuid) {
      selectedCommunity = communityId;
    } else if (
      values.communityId &&
      values.communityId !== Constants.emptyGuid
    ) {
      selectedCommunity = values.communityId;
    }

    return {
      name: values.name,
      communityId: selectedCommunity,
      pageSize: 10,
      pageNumber: 1,
      sortBy: sortBy,
    };
  };

  const handleSearch = (values: UserSearchForm) => {
    setSearchQuery(getUpdatedSearchQuery(values));
  };

  const handleCommunityChange = (
    formikProps: FormikProps<object>,
    value: string
  ) => {
    setSearchQuery(
      getUpdatedSearchQuery({
        ...(formikProps.values as UserSearchForm),
        communityId: value,
      })
    );
  };

  function handleNew() {
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);
  }

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      sortBy: prev.sortBy,
      pageNumber: 1,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  function userActionButtons(row: GridUserDto) {
    let actionButton: ControlDatagridActionButtonProps[] = [
      VfxLib.datagridActionButtons({ datagridStyles: styles }).edit,
      VfxLib.datagridActionButtons({ datagridStyles: styles }).view,
    ];
    if (row.canUnlock) {
      actionButton = [
        ...actionButton,
        VfxLib.datagridActionButtons({ datagridStyles: styles }).unlock,
      ];
    }
    if (row.canResetPassword) {
      actionButton = [
        ...actionButton,
        VfxLib.datagridActionButtons({ datagridStyles: styles }).reset_password,
      ];
    }
    if (row.canResendConfirmation) {
      actionButton = [
        ...actionButton,
        VfxLib.datagridActionButtons({ datagridStyles: styles })
          .resend_confirmation_email,
      ];
    }
    return actionButton;
  }

  const renderSearchContainer = () => {
    return (
      <SearchContainer
        initialValues={{
          name: searchQuery.name ?? Constants.emptyString,
          communityId: searchQuery.communityId ?? Constants.emptyGuid,
        }}
        onSearch={handleSearch}
        onClear={handleClear}
        enableReinitialize
      >
        {(formikProps: FormikProps<object>) => (
          <>
            {!communityId && (
                <Col lg={4}>
                  <Form.Dropdown
                    name={"communityId"}
                    label={"Community"}
                    blankOption={{
                      key: Constants.emptyGuid,
                      display: "--Select Community--",
                      value: Constants.emptyGuid,
                    }}
                    options={LookupDto.mapToOptions(props.lookups?.communities)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleCommunityChange(formikProps, event.target.value)
                    }
                  />
                </Col>
            )}
            <Col lg={4} className="ms-2">
              <Form.Text name={"name"} label={"Name"} />
            </Col>
          </>
        )}
      </SearchContainer>
    );
  };

  const renderUserDataGrid = () => {
    return (
      <Control.DataGrid
        dataId="userID"
        height="55vh"
        cols={[
          {
            data: "userID",
            title: "UserID",
            visible: false,
            columnType: "number",
            alignment: "right",
            sortable: false,
          },
          {
            data: "name",
            title: "Name",
            visible: true,
            columnType: "text",
            alignment: "left",
            width: "18%",
          },
          {
            data: "email",
            title: "Email",
            visible: true,
            columnType: "text",
            alignment: "left",
            width: "30%",
          },
          {
            data: "userRole",
            title: "Role",
            visible: true,
            columnType: "text",
            alignment: "left",
            width: "14%",
          },
          {
            data: "userStatus",
            title: "Status",
            visible: true,
            columnType: "text",
            alignment: "left",
            width: "14%",
          },
        ]}
        actionCol={{
          data: "id",
          title: "Action",
          visible: true,
          columnType: "action",
          alignment: "right",
          width: "100px",
          controlStyles: styles,
          actionButtons: (row) => userActionButtons(row),
        }}
        onCustomEvent={(actinType, dataId) =>
          props.onCustomEvent?.(
            actinType,
            dataId,
            userGridQuery?.data?.result?.find((x) => x.userID === dataId)
              ?.name
          )
        }
        data={userGridQuery?.data?.result ?? []}
        isActionButtonClick={true}
        totalRecords={
          userGridQuery.data?.totalSize ??
          userGridQuery?.data?.result?.length ??
          0
        }
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) =>
          setSearchQuery((query) => ({ ...query, ...patch }))
        }
        uiOptions={{
          title: communityId ? undefined : "Users",
          filterContent: renderSearchContainer(),
          primaryAction: props.isCommunityDeactive
            ? undefined
            : {
                text: "Add User",
                icon: "bi bi-plus-lg",
                onClick: handleNew,
              },
        }}
      />
    );
  };

  return (() => {
    if (communityId) {
      return (
        <>
          {renderUserDataGrid()}
        </>
      );
    } else {
      return (
        <PageContent hideHeader>
          {renderUserDataGrid()}
        </PageContent>
      );
    }
  })();
};
